import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import Panel from '../common/Panel';
import ListToolbar from '../common/ListToolbar';
import UnderlineTabs from '../common/UnderlineTabs';
import FilterPillRow from '../common/FilterPillRow';
import BottomSheetMultiSelect from '../common/BottomSheetMultiSelect';
import TableFooterSimple from '../common/TableFooterSimple';
import EmptyState from '../common/EmptyState';
import RefreshButton from '../common/RefreshButton';
import { SP } from '../../theme/tokens';

import FulfillmentTable from './FulfillmentTable';
import AllocationTable from './AllocationTable';
import FulfillmentUpload from './FulfillmentUpload';
import PackageDetailDrawer from './PackageDetailDrawer';
import { formatMonthLabel } from '../../utils/formatters';
import { formatMoney } from '../../utils/format';

const API = process.env.REACT_APP_API_BASE_URL || '';

/** Die zwei Inhaltsbereiche — Schluessel der Achse `tab` (§3.1). */
const TABS = ['orders', 'allocation'];
const TAB_LABELS = ['Orders', 'Allocation per SKU'];

/**
 * Die fuenf Sorten der Orders-Spalte (C3-4). 🔴 **Ohne `All`:** die Pille ist
 * ein `quick`-Filter mit `multi` — jeder gedrueckte Wert ist abwaehlbar, und
 * eine „All"-Pille waere eine sechste Option, die nichts einschraenkt.
 */
const KIND_OPTIONS = [
  { value: 'DN', label: 'Shipments' },
  { value: 'RT', label: 'Returns' },
  { value: 'IN', label: 'Inbounds' },
  { value: 'TO', label: 'Transfers' },
  { value: 'STORAGE', label: 'Storage' },
];

const ROWS_PER_PAGE = 50;

const normalize = (value, fallback) => (value != null && value !== '' ? value : fallback);

/**
 * FulfillmentTables — der Datenbereich des Fulfillment-Berichts.
 *
 * Zwei Inhaltsbereiche (Orders / Allocation per SKU) unter EINER Reiterreihe,
 * die **ueber** dem Panel steht (C3-1, P3): Reiter sind Inhaltsbereiche und
 * tragen deshalb keinen Zaehler. Nur der aktive Bereich ist gerendert, also
 * gibt es genau EIN `searchbox` (P34) und genau EINEN Zaehler im Panel (P8).
 *
 * 🔴 **Was hier NICHT mehr steht, und warum:** die handgebaute Filterzeile
 * (`BottomSheetMultiSelect`-Reihe) und das eigene Filter-Blatt. Beides ist der
 * Auftrag von `ListToolbar` (Z1/Z2/Z3, Desktop- und Telefonform, BS2/BS6/BS7/
 * BS8) — die Seite fuehrte daneben eine zweite Fassung, die auf dem Telefon
 * anders aussah als jede andere Liste. Ebenso entfallen der zweite Zaehler
 * (`{paged.length}/{filtered.length}`) und der lokale Seitenzustand: beides
 * kommt jetzt aus der Adresse (C3-7/C3-8).
 */
export default function FulfillmentTables({
  query,
  defaultMonths = [],
  months = [],
  carrierOptions = [],
  countries = [],
  carrierLoading,
  countriesLoading,
  orders = [],
  ordersTotal = null,
  ordersLoading = false,
  ordersError = false,
  ordersReady = false,
  allocRows = [],
  isRefreshing,
  allocLoading,
  onRefreshAll,
  onRetryOrders,
  onRebuildAllocation,
}) {
  const { state, set, chips, ignoredValues } = query;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tab = TABS.includes(state.tab) ? state.tab : TABS[0];
  const tabIndex = TABS.indexOf(tab);
  const showAllocation = tab === 'allocation';

  const [drawerRef, setDrawerRef] = useState(null);
  const [drawerSku, setDrawerSku] = useState(null);
  const [dedupeOpen, setDedupeOpen] = useState(false);
  const [dedupeData, setDedupeData] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [checksOpen, setChecksOpen] = useState(false);
  const [checks, setChecks] = useState(null);

  const q = state.q || '';
  const kindWahl = useMemo(() => (Array.isArray(state.kind) ? state.kind : []), [state.kind]);
  const monate = useMemo(() => (Array.isArray(state.months) ? state.months : []), [state.months]);
  const carrierWahl = useMemo(
    () => (Array.isArray(state.carriers) ? state.carriers : []),
    [state.carriers]
  );
  const countryWahl = useMemo(
    () => (Array.isArray(state.country) ? state.country : []),
    [state.country]
  );

  const monthOptions = useMemo(
    () => months.map((m) => ({ value: m, label: formatMonthLabel(m) })),
    [months]
  );
  const carrierOptionList = useMemo(
    () =>
      carrierOptions.map((c) => ({
        value: normalize(c, 'others'),
        label: c || 'Others',
      })),
    [carrierOptions]
  );
  const countryOptionList = useMemo(
    () =>
      countries.map((c) => ({
        value: normalize(c, 'Unknown'),
        label: c || 'Unknown',
      })),
    [countries]
  );

  /**
   * Die Clientfilter des Orders-Bereichs (§4.1) — **ohne** die Kind-Achse.
   *
   * 🔴 `months` steht hier NUR als Waechter: bei genau einem Monat ist der
   * Monat ein SERVERfilter (`?month=`), und die geladenen Zeilen gehoeren schon
   * dazu. Bei mehreren Monaten laedt der Server ungefiltert (Kappung), und
   * dieser Vergleich blendet die uebrigen aus. Ohne ihn zeigte die Liste bei
   * zwei gewaehlten Monaten auch die Zeilen des anderen.
   *
   * 🔴 **Warum die Kind-Achse hier FEHLT** (Regel-16-W1 der Runde 1): die
   * Kind-Pillen tragen die Zahl je Sorte (C3-4), und diese Zahl muss die Menge
   * VOR der Kind-Einschraenkung sein. Sonst zeigte „Shipments 0" genau dann,
   * wenn man „Returns" drueckt — eine Zahl, die die Pille selbst gerade
   * ausgeschlossen hat. Die Einschraenkung sitzt deshalb einen Schritt weiter
   * unten in `filteredOrders`.
   */
  const basisgefiltert = useMemo(
    () =>
      (orders || []).filter((row) => {
        const needle = q.trim().toLowerCase();
        if (needle) {
          const hay = [
            row.reference_number,
            row.order_number,
            row.carrier,
            row.order_country,
            row.ref_kind,
            row.month,
          ]
            .map((x) => (x || '').toString().toLowerCase())
            .join(' ');
          if (!hay.includes(needle)) return false;
        }
        if (monate.length && !monate.includes(row.month)) return false;
        if (carrierWahl.length && !carrierWahl.includes(normalize(row.carrier, 'others'))) return false;
        if (countryWahl.length && !countryWahl.includes(normalize(row.order_country, 'Unknown'))) {
          return false;
        }
        return true;
      }),
    [orders, q, monate, carrierWahl, countryWahl]
  );

  /** Die Menge, die die Tabelle zeigt: `basisgefiltert` **plus** die Kind-Achse. */
  const filteredOrders = useMemo(
    () =>
      // 🔴 Leere Liste heisst KEINE Einschraenkung (B3) — der frueher hier
      // verglichene Skalar war die Einfachauswahl, die es nicht mehr gibt.
      kindWahl.length
        ? basisgefiltert.filter((row) => kindWahl.includes((row.ref_kind || '').toUpperCase()))
        : basisgefiltert,
    [basisgefiltert, kindWahl]
  );

  /**
   * Die Zahl je Kind-Sorte — aus `basisgefiltert`, also **vor** der
   * Kind-Einschraenkung (Regel-16-W1, s. dort). Solange nichts geladen ist,
   * traegt die Pille **keine** Zahl: „noch nicht bekannt" ist nicht `0`
   * (Regel 22 §3).
   */
  const kindOptionen = useMemo(() => {
    const zahlen = {};
    basisgefiltert.forEach((row) => {
      const k = (row.ref_kind || '').toUpperCase();
      zahlen[k] = (zahlen[k] || 0) + 1;
    });
    return KIND_OPTIONS.map((opt) => ({
      ...opt,
      count: ordersReady ? (zahlen[opt.value] || 0) : undefined,
    }));
  }, [basisgefiltert, ordersReady]);

  // Sortierung aus der Adresse, Vorgabe `-total_net` (C3-9).
  const sort = state.sort || '-total_net';
  const sortAbsteigend = sort.startsWith('-');
  const sortFeld = sortAbsteigend ? sort.slice(1) : sort;
  const sortiert = useMemo(() => {
    const zahl = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const text = (v) => String(v ?? '');
    const numerisch = ['product_count', 'total_net'].includes(sortFeld);
    return [...filteredOrders].sort((a, b) => {
      const av = numerisch ? zahl(a[sortFeld]) : text(a[sortFeld]);
      const bv = numerisch ? zahl(b[sortFeld]) : text(b[sortFeld]);
      if (av < bv) return sortAbsteigend ? 1 : -1;
      if (av > bv) return sortAbsteigend ? -1 : 1;
      return 0;
    });
  }, [filteredOrders, sortFeld, sortAbsteigend]);

  // Seitenwechsel 50 (C3-8) — der Zustand liegt in der Adresse, die Klemmung
  // schreibt sie NICHT um (N10.4: die Ansicht zeigt die letzte Seite).
  const seiten = Math.max(1, Math.ceil(sortiert.length / ROWS_PER_PAGE));
  const seite = Math.min(Math.max(1, Number(state.page) || 1), seiten);
  const sichtbareZeilen = useMemo(
    () => sortiert.slice((seite - 1) * ROWS_PER_PAGE, seite * ROWS_PER_PAGE),
    [sortiert, seite]
  );

  /**
   * Panel-lokales „Clear all" (C3-16).
   *
   * 🔴 Der GETEILTE `clearAll` des Hooks loescht `tab` unbedingt — im
   * Allocation-Reiter sprang die Seite damit in den Orders-Reiter, mit den
   * alten `q`/`kind`-Filtern. Hier werden NUR die eigenen Dimensionen
   * zurueckgesetzt. `months` bekommt seinen ECHTEN Default (nicht `null`):
   * `set('months', null)` schriebe `?months=all` — „alle Monate" statt
   * „zurueck zum neuesten" (W10). `tab`/`sort` bleiben stehen; `page` faellt
   * auf 1, weil `set` das fuer jede Abfrageaenderung tut.
   */
  const clearPanel = useCallback(() => {
    set('q', null);
    set('kind', []);
    set('carriers', []);
    set('country', []);
    set('months', defaultMonths);
  }, [set, defaultMonths]);

  const blattAktiv =
    (monate.length ? 1 : 0) + (carrierWahl.length ? 1 : 0) + (countryWahl.length ? 1 : 0);

  // Der ERSTE Aufbau ist gesperrt, wenn noch nie etwas geladen wurde und der
  // Abruf gescheitert ist (N8.5). Ein spaeter gescheitertes Nachladen laesst
  // die Zeilen stehen und sagt es daneben.
  const sperre = !ordersReady && ordersError;

  const leerGrund = (() => {
    if (sperre) return 'error';
    if (ordersLoading && !ordersReady) return null;
    if (filteredOrders.length > 0) return null;
    const engtEin = q.trim() || kindWahl.length || carrierWahl.length || countryWahl.length || monate.length > 1;
    if (orders.length > 0 || engtEin) return 'no-match';
    return 'no-data';
  })();

  const leerzustand = (
    <Leerzustand
      grund={leerGrund}
      q={q}
      onRetry={onRetryOrders}
      onClearSearch={() => set('q', null)}
      onClearFilters={clearPanel}
    />
  );

  const kappung =
    ordersTotal != null && orders.length < ordersTotal
      ? 'Showing the 2,000 highest-value rows; filters apply to those rows. Pick a single month to load it in full.'
      : null;

  useEffect(() => {
    const onScan = () => setDedupeOpen(true);
    const onChecks = () => setChecksOpen(true);
    window.addEventListener('fulfill:scan', onScan);
    window.addEventListener('fulfill:checks', onChecks);
    return () => {
      window.removeEventListener('fulfill:scan', onScan);
      window.removeEventListener('fulfill:checks', onChecks);
    };
  }, []);

  useEffect(() => {
    if (!dedupeOpen) return;
    const load = async () => {
      setDedupeLoading(true);
      try {
        const r = await fetch(`${API}/api/fulfillment/dedupe/scan?limit=50`);
        const j = await r.json();
        setDedupeData(j);
      } catch {
        setDedupeData(null);
      } finally {
        setDedupeLoading(false);
      }
    };
    load();
  }, [dedupeOpen]);

  const ordersPanel = (
    <>
      {/* Ein gescheitertes NACHLADEN hat eine eigene Flaeche UEBER der Liste —
          nicht den Leerzustand: die Zeilen der letzten erfolgreichen Ladung
          stehen noch da, und ein Leerzustand wuerde sie verleugnen. Ausserhalb
          von `liste-panel`, damit P8 genau EINEN Zaehler im Panel findet. */}
      {ordersError && ordersReady && (
        <Alert
          severity="warning"
          sx={{ mb: SP.card }}
          action={(
            <Button color="inherit" size="small" onClick={onRetryOrders}>
              Try again
            </Button>
          )}
        >
          The list could not be refreshed. The rows below are the last successful load.
        </Alert>
      )}
      <Panel pad="none" data-testid="liste-panel">
      <Box sx={{ px: SP.card, pt: SP.card }}>
        {/* N8.5: der Grund der Sperre steht als SICHTBARER Text ueber der
            Leiste — `messeP9Sperre` liest genau diese Marke und verlangt den
            Satz „… locked until the list loads". */}
        {sperre && (
          <Alert severity="warning" sx={{ mb: SP.card }} data-testid="erster-aufbau-gesperrt">
            Search and filters are locked until the list loads. Use Try again below.
          </Alert>
        )}

        {/* 🔴 N10.4: ein Wert, den die Ansicht nicht kennt, wird VERWORFEN —
            und das wird gesagt. Ohne diesen Hinweis verschwaende ein geteilter
            Link still, und der Bediener hielte den Filter fuer kaputt. Der
            Hook liefert den Befund nur fuer den Durchlauf, in dem der Wert noch
            in der Adresse stand. */}
        {ignoredValues && ignoredValues.length > 0 && (
          <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mb: SP.card }}>
            Ignored an unknown filter value:{' '}
            {ignoredValues.map((v) => `${v.key}=${v.value}`).join(', ')}.
          </Alert>
        )}

        <ListToolbar
          shortcut="/"
          search={{
            value: q,
            // Puffermodus: 300 ms Ruhe oder Enter uebergeben den Begriff.
            onCommit: (v) => set('q', v),
            placeholder: 'Search reference, order, carrier, country…',
            label: 'Search reference, order, carrier, country',
            inputRole: 'searchbox',
            disabled: sperre,
            sx: { flex: 1, minWidth: 0 },
          }}
          // Genau DREI Facetten (die Leiste wirft bei > 3): Monat, Carrier,
          // Land — je mehrwertig, Desktop als Pille (BS7).
          facets={[
            <BottomSheetMultiSelect
              key="months"
              desktopForm="pill"
              label="Months"
              value={monate}
              onChange={(v) => set('months', v)}
              options={monthOptions}
              disabled={sperre}
            />,
            <BottomSheetMultiSelect
              key="carriers"
              desktopForm="pill"
              label="Carriers"
              value={carrierWahl}
              onChange={(v) => set('carriers', v)}
              options={carrierOptionList}
              disabled={sperre || carrierLoading}
            />,
            <BottomSheetMultiSelect
              key="country"
              desktopForm="pill"
              label="Country"
              value={countryWahl}
              onChange={(v) => set('country', v)}
              options={countryOptionList}
              disabled={sperre || countriesLoading}
            />,
          ]}
          moreFilters={{
            disabled: sperre,
            // Dieselben drei Kriterien wie die Z1-Facetten — ohne sie waeren
            // sie auf dem Telefon unerreichbar (die Leiste rendert Facetten
            // nie inline unter `sm`, N11.8).
            criteria: [
              {
                key: 'months',
                kind: 'select',
                label: 'Months',
                multiple: true,
                value: monate,
                onChange: (v) => set('months', v),
                options: monthOptions,
                emptyLabel: 'Any',
              },
              {
                key: 'carriers',
                kind: 'select',
                label: 'Carriers',
                multiple: true,
                value: carrierWahl,
                onChange: (v) => set('carriers', v),
                options: carrierOptionList,
                emptyLabel: 'Any',
              },
              {
                key: 'country',
                kind: 'select',
                label: 'Country',
                multiple: true,
                value: countryWahl,
                onChange: (v) => set('country', v),
                options: countryOptionList,
                emptyLabel: 'Any',
              },
            ],
            // Der Blatt-Zaehler kennt „noch nicht bekannt" (`null`) — eine 0
            // dort liest sich als „nichts da".
            resultCount: ordersReady ? filteredOrders.length : null,
            // N4.6: die BLATT-Kriterien mit Wert, einschliesslich der Vorgabe
            // (der neueste Monat engt ein und zaehlt deshalb mit).
            activeCount: blattAktiv,
            onClearAll: clearPanel,
          }}
          // Z2 — der Objektart-Filter, jetzt mehrwertig und OHNE „All"
          // (`alwaysShow` ist Pflicht: ohne es rendert die Reihe auf dem
          // Schreibtisch gar nicht, `FilterPillRow.jsx:175`).
          quickFilters={(
            <FilterPillRow
              alwaysShow
              multi
              value={kindWahl}
              onChange={(v) => set('kind', v)}
              // Die Zahl je Sorte (C3-4, Regel-16-W1) — aus der Menge VOR der
              // Kind-Einschraenkung; ohne geladene Zeilen gar keine Zahl.
              options={kindOptionen}
              disabled={sperre}
              label="Kind"
            />
          )}
          count={{
            noun: 'orders',
            // „Unbekannt" ist nie 0: `n` steht erst, wenn etwas geladen ist;
            // `m` kommt vom Server (Menge VOR der Kappung).
            n: ordersReady ? filteredOrders.length : null,
            // 🔴 **`m` NUR bei genau einem Monat** (Regel-15-W3 der Runde 1).
            // Der Server filtert den Monat nur dann (`?month=`); bei zwei
            // gewaehlten Monaten ist `total` die Menge ALLER Monate, und
            // „8 of 11848" nennt einen Nenner, der Monate enthaelt, die der
            // Nutzer gerade ausgeschlossen hat. Ohne `m` sagt der Zaehler
            // „8 orders", und der Kappungssatz darunter sagt den Rest.
            m: ordersReady && monate.length === 1 ? ordersTotal : null,
            loading: ordersLoading,
          }}
          refresh={(
            <RefreshButton ariaLabel="Refresh fulfillment tables" onClick={onRefreshAll} />
          )}
          // Z3 — die Chips und das PANEL-LOKALE „Clear all" (C3-16). Die
          // Ueberschreibung ist die kleinste Aenderung: `ListToolbar` reicht
          // genau dieses Feld an `ActiveFilterBar` weiter.
          query={{ chips, clearAll: clearPanel }}
        >
          {kappung && (
            <Box sx={{ px: SP.card, pt: SP.card }}>
              <Alert severity="info">{kappung}</Alert>
            </Box>
          )}

          <FulfillmentTable
            rows={sichtbareZeilen}
            loading={ordersLoading && !ordersReady}
            sort={sort}
            onSort={(next) => set('sort', next)}
            onRowClick={(row) => setDrawerRef(row?.reference_number || '')}
            leer={leerGrund ? leerzustand : null}
          />

          {/* Die Fusszeile steht NUR, wenn sie blaettert (N8.7): ohne Inhalt
              bliebe ein leerer, deckender Balken stehen. */}
          {filteredOrders.length > ROWS_PER_PAGE && (
            <Box data-testid="liste-fuss">
              <TableFooterSimple
                count={filteredOrders.length}
                page={seite - 1}
                onPageChange={(p) => set('page', p + 1)}
                rowsPerPage={ROWS_PER_PAGE}
                // BS17: mobil im Fluss, nicht ueber der schwebenden Unterleiste.
                stickyOnMobile={false}
              />
            </Box>
          )}
        </ListToolbar>
      </Box>
    </Panel>
    </>
  );

  const allocationPanel = (
    <Panel pad="none" data-testid="liste-panel">
      <Box sx={{ px: SP.card, pt: SP.card }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: SP.card }}>
          Sorted by total cost (highest first).
        </Typography>
      </Box>
      <AllocationTable
        rows={allocRows || []}
        loading={allocLoading || isRefreshing}
        onOpenSku={(sku) => setDrawerSku(sku)}
      />
    </Panel>
  );

  return (
    <>
      <Box data-testid="liste-reiter">
        <UnderlineTabs
          scrollable
          withPanel
          ariaLabel="Fulfillment report"
          panelIdPrefix="fulfillment"
          tabs={TAB_LABELS}
          value={tabIndex}
          onChange={(i) => set('tab', TABS[i])}
        >
          {showAllocation ? allocationPanel : ordersPanel}

          {/* 🔴 Upload und Dialoge liegen INNERHALB des Reiter-Paneels, aber
              AUSSERHALB von `liste-panel`: P8 zaehlt `role="status"` in der
              Liste, und die Upload-Karte ist keine Region der Liste. */}
          <Divider sx={{ mt: SP.card, mb: SP.card }} />
          <Box sx={{ px: SP.card, pb: SP.card }}>
            <FulfillmentUpload onUploaded={onRefreshAll} onRebuildAllocation={onRebuildAllocation} />
          </Box>
        </UnderlineTabs>
      </Box>

      <PackageDetailDrawer
        referenceNumber={drawerRef}
        onClose={() => setDrawerRef(null)}
      />
      <PackageDetailDrawer
        mode="sku"
        sku={drawerSku}
        onClose={() => setDrawerSku(null)}
        onOpenPackage={(ref) => setDrawerRef(ref)}
      />

      <Dialog open={dedupeOpen} onClose={() => setDedupeOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>Duplicate scan</DialogTitle>
        <DialogContent dividers>
          {!dedupeLoading && dedupeData ? (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Duplicates by table
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="ful_order_month"
                    secondary={`duplicates: ${dedupeData?.ful_order_month?.count ?? 0}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="ful_sku_month"
                    secondary={`duplicates: ${dedupeData?.ful_sku_month?.count ?? 0}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="ful_inbound_month"
                    secondary={`duplicates: ${dedupeData?.ful_inbound_month?.count ?? 0}`}
                  />
                </ListItem>
              </List>
            </>
          ) : (
            <Typography variant="body2">
              {dedupeLoading ? 'Scanning…' : 'No data.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDedupeOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={checksOpen} onClose={() => setChecksOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Data integrity checks</DialogTitle>
        <DialogContent dividers>
          {checks ? (
            <>
              <Typography variant="subtitle2">By kind (EUR)</Typography>
              <ul>
                {(checks.by_kind || []).map((k) => (
                  <li key={k.ref_kind}>
                    {k.ref_kind}: {formatMoney(k.total)}
                  </li>
                ))}
              </ul>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Shipments total (DN+RT):{' '}
                {formatMoney(checks.shipments_total)}
              </Typography>
              <Typography variant="body2">
                DN count: {checks.dn_count} · RT count: {checks.rt_count}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Rows in scope: {checks.row_count_scope}
              </Typography>
            </>
          ) : (
            <Typography variant="body2">No checks run yet.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChecksOpen(false)}>Close</Button>
          <Button
            onClick={async () => {
              const body = {
                months: monate,
                country: countryWahl[0] || null,
              };
              const res = await fetch(`${API}/api/fulfillment/debug/checks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
              });
              const j = await res.json();
              setChecks(j);
            }}
            variant="contained"
          >
            Run
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Die drei Leerzustaende an EINER Stelle.
 *
 * 🔴 `no-data` und `no-match` sind verschiedene Objekte: der eine sagt „es gibt
 * nichts", der andere „deine Auswahl trifft nichts" — und nur der zweite hat
 * einen Weg heraus. `error` traegt „Try again", weil ein gescheiterter Abruf
 * wiederholbar ist.
 */
function Leerzustand({ grund, q, onRetry, onClearSearch, onClearFilters }) {
  if (grund === 'error') {
    return (
      <EmptyState
        reason="error"
        message="Orders could not be loaded."
        onRetry={onRetry}
        // Diese Flaeche traegt ihre Aussage selbst (der globale Ausfallbanner
        // liegt unter jedem Modal) — der dokumentierte Opt-in-Fall.
        showDuringOutage
      />
    );
  }
  if (grund === 'no-match') {
    return (
      <EmptyState
        reason="no-match"
        message={q ? undefined : 'No order matches the current filters.'}
        query={q || undefined}
        noun="order"
        onClearSearch={q ? onClearSearch : undefined}
        onClearFilters={q ? undefined : onClearFilters}
      />
    );
  }
  return (
    <EmptyState
      reason="no-data"
      message="No orders in the selected month yet."
      detail="The list shows what the last LSM import wrote for the chosen month. Pick another month, or upload a statement below."
    />
  );
}
